import FunctionService from './function-service';
import { User } from '../../models/interfaces/user';
import OrganizationSubscriptionService from './organization-subscription-service';
import BillingService from './billing-service';
import moment from 'moment';
import { PlanTypeLabels } from '../../models/plan-type';
import { PaymentCycleLabel } from '../../models/payment-cycle';
import UserService from './user-service';
import { OrganizationSubscription } from '../../models/interfaces/organization-subscription';

const handleCancelSubscription = async (organizationId: string | undefined, user: User) => {
    if (!organizationId) {
        throw new Error("Organization ID is not provided");
    }

    const orgSub = await OrganizationSubscriptionService.getByOrganizationId(organizationId);

    if (orgSub.length === 0 || !orgSub[0].stripePlanSubscription || !orgSub[0].renewDate) {
        throw new Error("There was an error cancelling your subscription");
    }

    await BillingService.cancelSubscription({
        subscriptionId: orgSub[0].stripePlanSubscription,
        email: user.email!,
        planType: orgSub[0].plan?.value !== undefined ? PlanTypeLabels[orgSub[0].plan.value] : undefined,
        paymentCycle: orgSub[0].paymentCycle?.value !== undefined ? orgSub[0].paymentCycle.value : undefined,
        renewDate: orgSub[0].renewDate,
    });

    if (orgSub[0].plan?.value !== undefined && orgSub[0].paymentCycle?.value !== undefined) {
        const nextPaymentDate = new Date(orgSub[0].renewDate);
        const planInfo = {
            planTypeLabel: PlanTypeLabels[orgSub[0].plan.value],
            paymentCycleLabel: PaymentCycleLabel[orgSub[0].paymentCycle.value],
            planEndDate: moment(nextPaymentDate).format("MMMM DD, yyyy"),
        };
        await FunctionService.sendCancellationEmail(user, planInfo);
    }
};

const handleCancelUserSubscription = async (userId: string | undefined, user: User) => {
    if (!userId) {
        throw new Error("User ID is not provided");
    }

    const userRecord = await UserService.get(userId);

    if (!userRecord?.subscription?.subscriptionId) {
        throw new Error("There was an error cancelling your subscription");
    }

    await BillingService.cancelSubscription({
        subscriptionId: userRecord.subscription.subscriptionId,
        email: userRecord.email!,
        isUserSubscription: true,
    });

    await FunctionService.sendUserSubscriptionCancellationEmail(userRecord);
};

const handleSubscriptionReactivation = async (organizationId: string | undefined) => {
    if (!organizationId) {
        throw new Error("Organization ID is not provided");
    }

    const orgSub = await OrganizationSubscriptionService.getByOrganizationId(organizationId);

    if (orgSub.length === 0 || !orgSub[0].stripePlanSubscription) {
        throw new Error("There was an error reactivating your subscription");
    }

    await BillingService.reactivateSubscription({
        subscriptionId: orgSub[0].stripePlanSubscription,
    });
};

const handleUserSubscriptionReactivation = async (userId: string | undefined) => {
    if (!userId) {
        throw new Error("User ID is not provided");
    }

    const userRecord = await UserService.get(userId);

    if (!userRecord?.subscription?.subscriptionId) {
        throw new Error("There was an error reactivating your subscription");
    }

    await BillingService.reactivateSubscription({
        subscriptionId: userRecord.subscription.subscriptionId,
    });
};

const getCustomerPortalSession = async (
    user: User | null | undefined,
    orgSub: OrganizationSubscription | null | undefined,
    allowSubscriptionChange: boolean = true,
) => {
    if (!user?.email || !orgSub) {
        return;
    }

    const response = await BillingService.createBillingPortalSession({
        email: user.email,
        thirdPartyProductId: orgSub.thirdPartyProductId,
        mySymmioMonthlyPriceId: orgSub.mySymmioMonthlyPriceId,
        mySymmioYearlyPriceId: orgSub.mySymmioYearlyPriceId,
        preferredLanguage: user.preferredLanguage,
        allowSubscriptionChange,
        returnUrl: window.location.href,
    });

    if (response.url) {
        window.location.href = response.url;
    }
};

const SubscriptionService = {
    handleCancelSubscription,
    handleSubscriptionReactivation,
    handleUserSubscriptionReactivation,
    handleCancelUserSubscription,
    getCustomerPortalSession,
};

export default SubscriptionService;